import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Code,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconPencil, IconPlus, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { usePermissions } from '../auth/usePermissions';
import { useWarehouses } from '../warehouses/useWarehouses';
import { listSuppliers, type SupplierDto } from './api';
import { SupplierFormModal } from './SupplierFormModal';

const ALL_WAREHOUSES = 'all';

type Scope = 'active' | 'all';

/**
 * Supplier master — the current product's supplier list.
 *
 * The warehouse filter is the useful half of the domain assignment: it answers "who can I order from
 * for this site", which is the question a purchase order will ask.
 */
export function SuppliersRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [modalOpen, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<SupplierDto | null>(null);
  const [scope, setScope] = useState<Scope>('active');
  const [warehouseId, setWarehouseId] = useState<string>(ALL_WAREHOUSES);
  const [search, setSearch] = useState('');

  const canWrite = permissions.has('suppliers:write');
  const warehouses = useWarehouses();

  // Warehouse and active are applied server-side; the search runs over the fetched slice, which is
  // fine at supplier scale — this is hundreds of rows, not the hundreds of thousands locations can be.
  const query = useQuery({
    queryKey: ['suppliers', warehouseId, scope] as const,
    queryFn: ({ signal }) =>
      listSuppliers(
        {
          includeInactive: scope === 'all',
          warehouseId: warehouseId === ALL_WAREHOUSES ? undefined : warehouseId,
        },
        signal,
      ),
  });

  const warehouseOptions = useMemo(
    () => [
      { value: ALL_WAREHOUSES, label: t('suppliers.allWarehouses') },
      ...(warehouses.data ?? [])
        .filter((w) => w.isActive)
        .map((w) => ({ value: w.warehouseId, label: `${w.warehouseCode} — ${w.displayName}` })),
    ],
    [warehouses.data, t],
  );

  const rows = useMemo(() => {
    const all = query.data ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter(
      (s) =>
        s.supplierName.toLowerCase().includes(needle) ||
        s.supplierNumber.toLowerCase().includes(needle) ||
        (s.contactName?.toLowerCase().includes(needle) ?? false),
    );
  }, [query.data, search]);

  function startCreate() {
    setEditing(null);
    open();
  }

  function startEdit(supplier: SupplierDto) {
    setEditing(supplier);
    open();
  }

  function onSaved() {
    // Any warehouse slice may now be stale — a supplier's assignments can have changed.
    void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  }

  /**
   * The name is the headline rather than the number: people say "Acme", and the number is the thing
   * they look up afterwards. Warehouse count stays a labelled row because zero of them is a real
   * problem (the supplier cannot be ordered from anywhere) and a badge in the aside would compete
   * with the active/inactive state, which is the other thing worth seeing at a glance.
   */
  const columns: DataColumn<SupplierDto>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('suppliers.name'),
        card: 'title',
        render: (s) => s.supplierName,
      },
      {
        key: 'number',
        label: t('suppliers.number'),
        card: 'subtitle',
        render: (s) => <Code>{s.supplierNumber}</Code>,
      },
      {
        key: 'contact',
        label: t('suppliers.contact'),
        render: (s) => (
          <Stack gap={0}>
            <Text size="sm">{s.contactName ?? '—'}</Text>
            {s.phone && (
              <Text size="xs" c="dimmed">
                {s.phone}
              </Text>
            )}
          </Stack>
        ),
      },
      {
        key: 'location',
        label: t('suppliers.location'),
        render: (s) => (
          <Text size="sm">{[s.city, s.stateProvince].filter(Boolean).join(', ') || '—'}</Text>
        ),
      },
      {
        key: 'terms',
        label: t('suppliers.terms'),
        render: (s) => <Text size="sm">{s.paymentTermCode ?? '—'}</Text>,
      },
      {
        key: 'warehouses',
        label: t('suppliers.warehouses'),
        // Zero is worth calling out: a supplier assigned to nothing cannot be ordered from
        // anywhere, which is easy to do by accident.
        render: (s) =>
          s.warehouseCount === 0 ? (
            <Badge size="sm" variant="light" color="yellow">
              {t('suppliers.noWarehouses')}
            </Badge>
          ) : (
            <Text size="sm">{s.warehouseCount}</Text>
          ),
      },
      {
        key: 'status',
        label: t('suppliers.status'),
        card: 'aside',
        render: (s) =>
          s.isActive ? (
            <Text size="sm" c="dimmed">
              {t('suppliers.activeState')}
            </Text>
          ) : (
            <Badge size="sm" variant="light" color="gray">
              {t('suppliers.inactiveState')}
            </Badge>
          ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('nav.suppliers')}
        actions={
          canWrite ? (
            <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={startCreate}>
              {t('suppliers.new')}
            </Button>
          ) : null
        }
      />

      <Stack gap="md">
        <Group gap="sm" wrap="wrap" align="flex-end">
          <TextInput
            label={t('suppliers.search')}
            placeholder={t('suppliers.searchPlaceholder')}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={{ base: '100%', sm: 280 }}
          />
          <Select
            label={t('suppliers.filterWarehouse')}
            data={warehouseOptions}
            value={warehouseId}
            onChange={(v) => setWarehouseId(v ?? ALL_WAREHOUSES)}
            disabled={warehouses.isLoading}
            w={{ base: '100%', sm: 260 }}
          />
          <Stack gap={4}>
            <Text size="sm" fw={500}>
              {t('suppliers.show')}
            </Text>
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as Scope)}
              data={[
                { label: t('suppliers.activeOnly'), value: 'active' },
                { label: t('suppliers.all'), value: 'all' },
              ]}
            />
          </Stack>
        </Group>

        {query.isLoading && (
          <Center py="xl">
            <Loader color="invetrak" />
          </Center>
        )}

        {query.isError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title={t('suppliers.errors.loadFailed')}
          >
            <Stack gap="xs">
              <Text size="sm">{(query.error as Error).message}</Text>
              {query.error instanceof ApiError && query.error.problem.correlationId && (
                <Text size="xs" c="dimmed">
                  {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
                </Text>
              )}
              <Group>
                <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                  {t('suppliers.retry')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {query.isSuccess && rows.length === 0 && (
          <Alert color="gray" variant="light">
            {search.trim() ? t('suppliers.noMatch') : t('suppliers.empty')}
          </Alert>
        )}

        {query.isSuccess && rows.length > 0 && (
          <ResponsiveTable
            rows={rows}
            columns={columns}
            rowKey={(s) => s.supplierId}
            minWidth={820}
            actionsWidth={60}
            striped
            highlightOnHover
            actions={
              canWrite
                ? (s) => (
                    <ActionIcon
                      variant="subtle"
                      color="invetrak"
                      aria-label={t('suppliers.edit', { name: s.supplierName })}
                      onClick={() => startEdit(s)}
                    >
                      <IconPencil size={16} />
                    </ActionIcon>
                  )
                : undefined
            }
          />
        )}
      </Stack>

      <SupplierFormModal opened={modalOpen} onClose={close} supplier={editing} onSaved={onSaved} />
    </>
  );
}
